import { Component, OnInit, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { ImportantUpdatesService } from './important-updates.service';

type SourceKey =
  | 'ndaapprovals'
  | 'andaapprovals'
  | 'newdosageform'
  | 'tentativeandaapproval'
  | 'labelling'
  | 'newapprovedstrength'
  | 'updatedbeguidance'
  | 'newlistedpatentsdatabase'
  | 'delistedpatentsdatabase'
  | 'newApprovedIndication';

type BEBucket = 'newly_added' | 'newly_revised';

@Component({
  selector: 'app-important-updates',
  standalone: false,
  templateUrl: './important-updates.component.html',
  styleUrl: './important-updates.component.scss'
})
export class ImportantUpdatesComponent implements OnInit {
  @ViewChild('updatesTable') updatesTable!: Table;

  sourceOptions = [
    { label: 'NDA approvals', value: 'ndaapprovals' },
    { label: 'ANDA approvals', value: 'andaapprovals' },
    { label: '505B(2) approvals', value: 'newdosageform' },
    { label: 'Tentative ANDA Approvals', value: 'tentativeandaapproval' },
    { label: 'Labelling updates', value: 'labelling' },
    { label: 'New Approved Strength', value: 'newapprovedstrength' },
    { label: 'Update BE Guidances', value: 'updatedbeguidance' },
    { label: 'Delisted Patents', value: 'delistedpatentsdatabase' },
    { label: 'New Listed Patents Database', value: 'newlistedpatentsdatabase' },
    { label: 'New Approved Indication', value: 'newApprovedIndication' },
  ];
  selectedSource: SourceKey = 'ndaapprovals';

  /** BE segmented state (only used for updatedbeguidance) */
  beBucket: BEBucket = 'newly_added';

  /** dynamic title shown in purple header */
  sourceTitleMap: Record<SourceKey, string> = {
    ndaapprovals: 'NDA approvals',
    andaapprovals: 'ANDA approvals',
    newdosageform: '505B(2) approvals',
    tentativeandaapproval: 'Tentative ANDA Approvals',
    labelling: 'Labelling updates',
    newapprovedstrength: 'New Approved Strength',
    updatedbeguidance: 'Update BE Guidances',
    newlistedpatentsdatabase: 'New Listed Patents Database',
    delistedpatentsdatabase: 'Delisted Patents',
    newApprovedIndication: 'New Approved Indication',
  };

  /** computed title (adds suffix for BE segment) */
  get computedSourceTitle(): string {
    if (this.selectedSource !== 'updatedbeguidance') {
      return this.sourceTitleMap[this.selectedSource];
    }
    const suffix = this.beBucket === 'newly_revised' ? ' - Newly Revised' : ' - Newly Added';
    return this.sourceTitleMap['updatedbeguidance'] + suffix;
  }

  private columnsBySource: Record<SourceKey, { field: string; header: string }[]> = {
    ndaapprovals: [
      { field: 'brand_name', header: 'Brand Name' },
      { field: 'Active_Ingredients', header: 'Active Ingredients' },
      { field: 'dosage_forms', header: 'Dosage Form' },
      { field: 'roa', header: 'ROA' },
      { field: 'Company', header: 'Company' },
      { field: 'nda_number', header: 'NDA Number' },
      { field: 'Approval_Date', header: 'Approval Date' },
      { field: 'uniq_number', header: 'Unique Number' },
    ],
    andaapprovals: [
      { field: 'brand_name', header: 'Brand Name' },
      { field: 'Active_Ingredients', header: 'Active Ingredients' },
      { field: 'dosage_forms', header: 'Dosage Form' },
      { field: 'roa', header: 'ROA' },
      { field: 'strength', header: 'Strength' },
      { field: 'Company', header: 'Company' },
      { field: 'anda_number', header: 'ANDA Number' },
      { field: 'Approval_Date', header: 'Approval Date' },
      { field: 'uniq_number', header: 'Unique Number' },
    ],
    newdosageform: [
      { field: 'brand_name', header: 'Brand Name' },
      { field: 'Active_Ingredient', header: 'Active Ingredients' },
      { field: 'dosage_forms', header: 'Dosage Form' },
      { field: 'roa', header: 'ROA' },
      { field: 'company', header: 'Company' },
      { field: 'strength', header: 'Strength' },
      { field: 'nda_number', header: 'NDA Number' },
      { field: 'Approval_Date', header: 'Approval Date' },
      { field: 'uniq_number', header: 'Unique Number' },
    ],
    tentativeandaapproval: [
      { field: 'nda_number', header: 'NDA Number' },
      { field: 'brand_name', header: 'Brand Name' },
      { field: 'Active_Ingredients', header: 'Active Ingredients' },
      { field: 'roa', header: 'ROA' },
      { field: 'dosage_forms', header: 'Dosage Form' },
      { field: 'strength', header: 'Strength' },
      { field: 'anda_number', header: 'ANDA Number' },
      { field: 'Company', header: 'Company' },
      { field: 'Approval_Date', header: 'Approval Date' },
      { field: 'uniq_number', header: 'Unique Number' },
    ],
    labelling: [
      { field: 'nda_number', header: 'NDA Number' },
      { field: 'brand_name', header: 'Brand Name' },
      { field: 'Active_Ingredient', header: 'Active Ingredients' },
      { field: 'Dosage_forms', header: 'Dosage Form' },
      { field: 'Application_Type', header: 'Application Type' },
      { field: 'Database_Updated', header: 'Last Updated on' },
      { field: 'uniq_number', header: 'Unique Number' },
    ],
    newapprovedstrength: [
      { field: 'nda_number', header: 'NDA Number' },
      { field: 'brand_name', header: 'Brand Name' },
      { field: 'Active_Ingredients', header: 'Active Ingredients' },
      { field: 'roa', header: 'ROA' },
      { field: 'dosage_forms', header: 'Dosage Form' },
      { field: 'Strength', header: 'Strength' },
      { field: 'ApprovalDate', header: 'Approval Date' },
      { field: 'uniq_number', header: 'Unique Number' },
    ],

    /** 🔁 MATCHES updatedbeguidance payload keys exactly */
    updatedbeguidance: [
      { field: 'nda_number', header: 'NDA Number' },
      { field: 'brand_name', header: 'Brand Name' },
      { field: 'Active_Ingredient', header: 'Active Ingredient' },
      { field: 'Route', header: 'ROA' },
      { field: 'Dosage_Form', header: 'Dosage Form' },
      { field: 'Date_Recommended', header: 'Date Recommended' },
      { field: 'uniq_number', header: 'Unique Number' },
    ],

    newlistedpatentsdatabase: [
      { field: 'nda_number', header: 'NDA Number' },
      { field: 'brand_name', header: 'Brand Name' },
      { field: 'Active_Ingredient', header: 'Active Ingredients' },
      { field: 'Route_of_Administration', header: 'ROA' },
      { field: 'Dosage_forms', header: 'Dosage Form' },
      { field: 'Strength', header: 'Strength' },
      { field: 'Patent_No', header: 'Patent No' },
      { field: 'Patent_Expire_Date', header: 'Patent Expire Date' },
      { field: 'Submission_Date', header: 'Submission Date' },
      { field: 'uniq_number', header: 'Unique Number' },
    ],
    delistedpatentsdatabase: [
      { field: 'nda_number', header: 'NDA Number' },
      { field: 'brand_name', header: 'Brand Name' },
      { field: 'Active_Ingredient', header: 'Active Ingredients' },
      { field: 'Route_of_Administration', header: 'ROA' },
      { field: 'Dosage_forms', header: 'Dosage Form' },
      { field: 'Patent_No', header: 'Patent No' },
      { field: 'Strength', header: 'Strength' },
      { field: 'Submission_Date', header: 'Submission Date' },
      { field: 'uniq_number', header: 'Unique Number' },
    ],
    newApprovedIndication: [
      { field: 'nda_number', header: 'NDA Number' },
      { field: 'brand_name', header: 'Brand Name' },
      { field: 'Active_Ingredient', header: 'Active Ingredients' },
      { field: 'Dosage_Route', header: 'Dosage Form; Route' },
      { field: 'Company_Name', header: 'Company' },
      { field: 'Approval_Date', header: 'Approval Date' },
      { field: 'Indication_Details', header: 'Indication Details' },
      { field: 'Category', header: 'Category' },
    ]
  };

  loading = false;
  page = 1;
  pageSize = 10;
  totalRecords = 0;

  rows: any[] = [];
  filteredRows: any[] = [];
  pagedRows: any[] = [];

  uiColumns: { field: string; header: string }[] = [];
  globalSearch = '';

  // optional info dialog
  infoDialog = false;
  infoMessage = '';

  // Indication dialog
  indicationVisible = false;
  indicationTitle = '';
  indicationContent = '';

  constructor(private api: ImportantUpdatesService) { }

  ngOnInit(): void {
    this.load(true);
  }

  onSourceChange() {
    // reset BE segment if switching to BE or away from it
    this.beBucket = 'newly_added';
    this.page = 1;
    this.globalSearch = '';
    this.updatesTable?.clear?.();
    this.load(true);
  }

  /** BE segment change handler */
  onBeBucketChange(bucket: BEBucket) {
    if (this.beBucket === bucket) return;
    this.beBucket = bucket;
    // re-run filter/paging only; rows already contain both buckets
    this.applySearch(); // will re-slice and re-count
  }

  onPage(e: { first: number; rows: number }) {
    this.pageSize = e.rows ?? this.pageSize;
    this.page = Math.floor((e.first ?? 0) / this.pageSize) + 1;
    this.sliceForPage();
  }

  applySearch() {
    const q = (this.globalSearch || '').toLowerCase().trim();

    // base set
    let base = [...this.rows];

    // BE: filter by selected bucket (tagged as __group)
    if (this.selectedSource === 'updatedbeguidance') {
      base = base.filter((r) => (r.__group as BEBucket) === this.beBucket);
    }

    // global search
    this.filteredRows = !q
      ? base
      : base.filter((r) => JSON.stringify(r).toLowerCase().includes(q));

    this.totalRecords = this.filteredRows.length;
    this.page = 1;
    this.sliceForPage();
  }

  toggleLive(row: any) {
    const uniq =
      row.uniq_number ?? row.uniqNumber ?? row.UNIQ_NUMBER ?? row.id ?? null;

    if (!uniq) {
      this.infoMessage = 'Unique identifier not found in this row.';
      this.infoDialog = true;
      return;
    }

    row.__busy = true;
    const next = !row.is_in_live;
    const all_live = false

    this.api.updateIsInLive(String(uniq), next, all_live, this.sourceTitleMap[this.selectedSource]).subscribe({
      next: () => {
        row.is_in_live = next;
        row.__busy = false;
      },
      error: () => {
        row.__busy = false;
        this.infoMessage = 'Failed to update. Please try again.';
        this.infoDialog = true;
      },
    });
  }

  /** internal */
  private load(resetColumns = false) {
    this.loading = true;
    this.api.fetch(this.selectedSource).subscribe({
      next: (payload: any) => {
        const arr = this.extractRows(this.selectedSource, payload);

        this.rows = (arr ?? []).map((x: any) => ({
          ...x,
          is_in_live: typeof x?.is_in_live === 'boolean' ? x.is_in_live : false,
        }));

        if (resetColumns) this.updatesTable?.clear?.();

        const allowed = this.columnsBySource[this.selectedSource] || [];
        this.uiColumns = this.pruneToPresentFields(allowed, this.rows);

        // apply bucket + search + paging
        this.applySearch();
        this.loading = false;
      },
      error: () => {
        this.rows = [];
        this.filteredRows = [];
        this.pagedRows = [];
        this.totalRecords = 0;
        this.loading = false;
      },
    });
  }

  /** normalize payload into flat array; BE items get __group tagged */
  private extractRows(source: SourceKey, payload: any): any[] {
    // typical { data: [...] }
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;

    // special shape for BE guidances
    if (source === 'updatedbeguidance') {
      const bucket = payload?.data ?? payload;
      const bucketKeys: BEBucket[] = ['newly_added', 'newly_revised'];
      let merged: any[] = [];

      for (const k of bucketKeys) {
        const list = bucket?.[k];
        if (Array.isArray(list) && list.length) {
          merged = merged.concat(list.map((x) => ({ ...x, __group: k })));
        }
      }
      if (merged.length) return merged;
    }

    // fallback: flatten any object-of-arrays under data
    if (payload?.data && typeof payload.data === 'object') {
      const arrays = Object.values(payload.data).filter(Array.isArray) as any[][];
      if (arrays.length) return ([] as any[]).concat(...arrays);
    }
    return [];
  }

  private sliceForPage() {
    const start = (this.page - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.pagedRows = this.filteredRows.slice(start, end);
  }

  private pruneToPresentFields(
    cols: { field: string; header: string }[],
    rows: any[]
  ) {
    if (!rows?.length) return cols;

    // Check if a field exists in *any* row of the data
    return cols.filter((c) => rows.some(r => r.hasOwnProperty(c.field) || r[c.field] !== undefined));
  }

  formatCell(v: any) {
    if (Array.isArray(v)) return v.join(', ');
    if (v === null || v === undefined || v === '') return 'N/A';
    return v;
  }

  bulkToggleLive(makeLive: boolean) {
    if (!this.rows?.length) {
      this.infoMessage = 'No records available to update.';
      this.infoDialog = true;
      return;
    }

    const uniqList = this.rows
      .map(r => r.uniq_number ?? r.uniqNumber ?? r.UNIQ_NUMBER ?? r.id)
      .filter(Boolean);

    if (!uniqList.length) {
      this.infoMessage = 'No valid unique numbers found.';
      this.infoDialog = true;
      return;
    }

    const actionText = makeLive ? 'Push All to Live' : 'Remove All from Live';
    this.loading = true;

    this.api.updateIsInLive(
      uniqList,
      makeLive,
      true,
      this.sourceTitleMap[this.selectedSource]
    ).subscribe({
      next: () => {
        this.rows.forEach(r => r.is_in_live = makeLive);
        this.applySearch();
        this.loading = false;

        this.infoMessage = `${actionText} completed successfully.`;
        this.infoDialog = true;
      },
      error: () => {
        this.loading = false;
        this.infoMessage = `Failed to ${actionText}. Please try again.`;
        this.infoDialog = true;
      }
    });
  }

  showIndication(row: any, field: string) {
    if (field === 'Indication_Details') {
      this.indicationTitle = 'Indication Details';
    } else {
      this.indicationTitle = field === 'Old_Indication' ? 'Old Indication' : 'New Indication';
    }
    this.indicationContent = row[field];
    this.indicationVisible = true;
  }
}